import { PHASE_LABELS, type PhaseType } from '~/models/phase'
import type { RulesOutcome } from '~/models/rulesOpinion'
import styles from './DecisionPanel.module.css'

/**
 * Project Sola's own weekly opinion (docs/ui-ux-spec.md, "Weekly Review").
 *
 * One recommendation, one primary action, the confidence and why, the evidence,
 * what was missing, and what was considered and rejected. Nothing here is
 * opaque: rules-engine.md requires that a recommendation can always be
 * explained afterwards.
 *
 * The three outcomes render as three genuinely different things. An
 * unimplemented phase gets an explicit "not available in this build" state with
 * **no recommendation, no action and no confidence value** — it must never look
 * like advice (D-043).
 */
export function DecisionPanel({ outcome }: { outcome: RulesOutcome }) {
  if (outcome.outcome === 'contextUnavailable') {
    return (
      <section class={styles.panel} aria-labelledby="decision-heading">
        <h2 id="decision-heading" class={styles.heading}>
          This week
        </h2>
        <p class={styles.body}>
          Project Sola needs an active phase before it can read a week. Set one up to see a
          recommendation here.
        </p>
      </section>
    )
  }

  if (outcome.outcome === 'phaseRulesUnavailable') {
    const label = PHASE_LABELS[outcome.phaseType as PhaseType] ?? outcome.phaseType

    return (
      <section class={styles.panel} aria-labelledby="decision-heading">
        <h2 id="decision-heading" class={styles.heading}>
          This week
        </h2>
        <p class={styles.body}>
          Recommendations are not available for {label} in this build. Project Sola will not apply
          another phase's logic to your week, so it is not guessing instead.
        </p>
      </section>
    )
  }

  /**
   * A domain with no rules (D-090).
   *
   * **The typechecker required this branch, and that is the point.** Adding
   * `DomainRulesUnavailable` to `RulesOutcome` broke every place that had narrowed
   * by excluding the two terminal outcomes it knew about — so a third one could
   * not be forgotten and silently fall through to the code that reads
   * `primaryAction`. A screen rendering `undefined` as an action is the failure
   * this union shape exists to prevent.
   *
   * Like the two above it: no action, no confidence, and the reason comes from
   * the outcome rather than being written again here.
   */
  if (outcome.outcome === 'domainRulesUnavailable') {
    return (
      <section class={styles.panel} aria-labelledby="decision-heading">
        <h2 id="decision-heading" class={styles.heading}>
          This week
        </h2>
        <p class={styles.body}>{outcome.reason}</p>
      </section>
    )
  }

  return (
    <section class={styles.panel} aria-labelledby="decision-heading">
      <h2 id="decision-heading" class={styles.heading}>
        This week
      </h2>

      <p class={styles.primaryAction}>{outcome.primaryAction}</p>

      {outcome.supportingActions.length === 0 ? null : (
        <ul class={styles.supporting}>
          {outcome.supportingActions.map((action) => (
            <li key={action}>{action}</li>
          ))}
        </ul>
      )}

      {/* Confidence is stated as a word plus its reason, never a bare colour or
          a bar — it is a qualitative label, not a probability. */}
      <p class={styles.confidence}>
        <span class={styles.confidenceLabel}>Confidence: {outcome.confidence}.</span>{' '}
        {outcome.confidenceReason}
      </p>

      <Details summary="What this is based on">
        <ul class={styles.list}>
          {outcome.evidence.map((item) => (
            <li key={item.statement}>{item.statement}</li>
          ))}
        </ul>
      </Details>

      {outcome.watchItems.length === 0 ? null : (
        <Details summary="Worth watching">
          <ul class={styles.list}>
            {outcome.watchItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </Details>
      )}

      {outcome.missingEvidence.length === 0 ? null : (
        <Details summary="What Project Sola could not see">
          <ul class={styles.list}>
            {outcome.missingEvidence.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </Details>
      )}

      {outcome.rejectedAlternatives.length === 0 ? null : (
        <Details summary="What it considered and did not choose">
          <ul class={styles.list}>
            {outcome.rejectedAlternatives.map((item) => (
              <li key={item.action}>
                <strong class={styles.rejectedAction}>{item.action}.</strong> {item.reason}
              </li>
            ))}
          </ul>
        </Details>
      )}

      {/* Provenance, and it has to stay true as the engine grows. It said "fat
          loss only" until Checkpoint 2.4 implemented the other four families,
          at which point it was telling a user on a lean bulk that their week
          had been judged by rules that had not run. It does not claim the
          engine is finished either — that claim belongs to Checkpoint 2.7. */}
      <p class={styles.provenance}>
        Judged by the rules for your current phase, on weight and waist. Rules applied:{' '}
        {outcome.triggeredRules.join(', ')} ({outcome.engineVersion}).
      </p>
    </section>
  )
}

/** Native disclosure: keyboard and screen-reader behaviour for free. */
function Details({ summary, children }: { summary: string; children: preact.ComponentChildren }) {
  return (
    <details class={styles.details}>
      <summary class={styles.summary}>{summary}</summary>
      {children}
    </details>
  )
}
